import React, { useState, useRef, useEffect, useMemo } from 'react';
import svgPaths from "./imports/svg-fcyeews8dc";
import { AllDesignsView } from './components/AllDesignsView';
import { UploadedFilesView } from './components/UploadedFilesView';
import { EmptyStateView } from './components/EmptyStateView';
import { getAllStates } from '../../services/indexedDB';
import { useGetSavedTemplatesQuery } from '../../features/templates/templatesApi';
import { useGetAssetsQuery, useUploadAssetMutation } from '../../features/assets/assetsApi';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

function Wrapper({ children, additionalClassNames = "" }: React.PropsWithChildren<{ additionalClassNames?: string }>) {
  return (
    <div className={clsx("relative shrink-0 size-[16px]", additionalClassNames)}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        {children}
      </svg>
    </div>
  );
}

function Frame({ children }: React.PropsWithChildren) {
  return (
    <Wrapper additionalClassNames="relative shrink-0 size-[16px]">
      <g id="Frame">{children}</g>
    </Wrapper>
  );
}

function Text({ text, additionalClassNames = "" }: { text: string; additionalClassNames?: string }) {
  return (
    <div style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }} className={clsx("flex flex-col justify-center leading-[0] relative shrink-0 text-[13px] text-nowrap", additionalClassNames)}>
      <p className="leading-[normal]">{text}</p>
    </div>
  );
}

function SidebarItem({
  active,
  onClick,
  icon,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: string
}) {
  return (
    <div
      className="relative rounded-[6px] shrink-0 w-full cursor-pointer transition-all group overflow-hidden h-[44px]"
      onClick={onClick}
    >
      {/* Active State Indicator */}
      <div 
          className="app-selector-indicator" 
          style={{ '--background-width': active ? '100%' : '0%' } as React.CSSProperties} 
      />

      <div className="flex flex-row items-center size-full relative z-10">
        <div className="content-stretch flex items-center justify-between p-[12px] relative w-full">
          <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
            {icon}
            <Text text={label} additionalClassNames={clsx("transition-colors duration-400", active ? "app-active-state font-sans font-medium" : "font-sans font-normal text-[#1d2739]")} />
          </div>
          <Text text={count} additionalClassNames={clsx("transition-colors duration-400", active ? "app-active-state font-sans font-normal" : "font-sans font-normal text-[#344054]")} />
        </div>
      </div>
    </div>
  );
}

export default function MyProjectsPage() {
  const [viewMode, setViewMode] = useState<'empty' | 'content'>('content');
  const [currentView, setCurrentView] = useState<'all-designs' | 'uploaded-files'>('all-designs');
  const { data: assetsData, isLoading: isAssetsLoading } = useGetAssetsQuery();
  const [uploadAsset] = useUploadAssetMutation();
  const { data: remoteTemplates } = useGetSavedTemplatesQuery();
  const [localStates, setLocalStates] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const getCount = async () => {
      try {
        const states = await getAllStates();
        setLocalStates(states);
      } catch (err) {
        console.error(err);
      }
    };
    getCount();
  }, []);

  const totalSavedCount = useMemo(() => {
    const remoteTotal = remoteTemplates?.meta?.total || 0;
    const remoteIds = new Set(remoteTemplates?.data?.map(t => t._id) || []);

    // Count local designs that are NOT already in the remote list (to avoid duplicates)
    const uniqueLocalCount = localStates.filter(ls => ls.id && !remoteIds.has(ls.id)).length;

    return remoteTotal + uniqueLocalCount;
  }, [remoteTemplates, localStates]);

  const uploadedFiles = useMemo(() => {
    return (assetsData?.data || []).map(asset => {
      let mappedType: 'Image' | 'Video' | 'Vector' = 'Image';
      if (asset.type === 'video') mappedType = 'Video';
      if (asset.type === 'vector') mappedType = 'Vector';

      return {
        id: asset._id,
        name: asset.name,
        type: mappedType,
        size: asset.size ? `${(asset.size / (1024 * 1024)).toFixed(1)}mb` : '0mb',
        src: asset.url
      };
    });
  }, [assetsData]);

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (const file of Array.from(files)) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('name', file.name);
          await uploadAsset(formData).unwrap();
        } catch (err) {
          console.error('Upload failed:', err);
        }
      }
      setViewMode('content');
      setCurrentView('uploaded-files');
    }
  };

  const handleStartCreating = () => {
    setViewMode('content');
    setCurrentView('all-designs');
  };

  return (
    <div className="flex-1 overflow-hidden size-full">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        onChange={handleFileChange}
        accept="image/*,video/*"
      />

      {viewMode === 'empty' ? (
        <EmptyStateView onUpload={triggerUpload} onStartCreating={handleStartCreating} />
      ) : (
        <div className="flex h-full gap-[24px]">
          {/* SIDEBAR */}
          <div className="w-[224px] shrink-0 relative h-full">
            <div className="content-stretch flex flex-col gap-[16px] items-start p-[12px] relative rounded-[8px] h-full bg-white shadow-sm border border-gray-100">
              <div className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#1d2739] text-[16px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                <p className="leading-[normal]">My Projects</p>
              </div>
              <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full overflow-y-auto">
                <SidebarItem
                  active={currentView === 'all-designs'}
                  onClick={() => setCurrentView('all-designs')}
                  label="All Designs"
                  count={totalSavedCount.toString()}
                  icon={
                    <Frame>
                      <path d={svgPaths.pff0fc00} stroke={currentView === 'all-designs' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #101928)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'all-designs' ? "1.25" : "1"} />
                      <path d={svgPaths.p36962800} stroke={currentView === 'all-designs' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #101928)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'all-designs' ? "1.25" : "1"} />
                      <path d={svgPaths.p19b58400} stroke={currentView === 'all-designs' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #101928)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'all-designs' ? "1.25" : "1"} />
                      <path d={svgPaths.p39897300} stroke={currentView === 'all-designs' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #101928)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'all-designs' ? "1.25" : "1"} />
                    </Frame>
                  }
                />
                <SidebarItem
                  active={currentView === 'uploaded-files'}
                  onClick={() => setCurrentView('uploaded-files')}
                  label="Uploaded Files"
                  count={uploadedFiles.length.toString()}
                  icon={
                    <Frame>
                      <path d={svgPaths.p12e6e1f0} stroke={currentView === 'uploaded-files' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #1D2739)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'uploaded-files' ? "1.25" : "1"} />
                      <path d={svgPaths.p3f60d880} stroke={currentView === 'uploaded-files' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #1D2739)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'uploaded-files' ? "1.25" : "1"} />
                      <path d="M8 8V12" stroke={currentView === 'uploaded-files' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #1D2739)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'uploaded-files' ? "1.25" : "1"} />
                      <path d="M10 10L8 8L6 10" stroke={currentView === 'uploaded-files' ? "var(--stroke-0, #227735)" : "var(--stroke-0, #1D2739)"} strokeLinecap="round" strokeLinejoin="round" strokeWidth={currentView === 'uploaded-files' ? "1.25" : "1"} />
                    </Frame>
                  }
                />
              </div>
            </div>
          </div>

          {/* CONTENT */}
          <div className="flex-1 overflow-y-auto">
            {currentView === 'all-designs' ? (
              <AllDesignsView />
            ) : isAssetsLoading ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Loader2 className="w-10 h-10 text-[#30A84B] animate-spin mb-4" />
                <p className="text-gray-500 font-medium">Fetching your assets...</p>
              </div>
            ) : (
              <UploadedFilesView files={uploadedFiles} onUpload={triggerUpload} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

